export type ProjectStatus = "active" | "paused" | "offboarded";

export interface Project {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  lastPingedAt: string;
  // SIM-1792 Slice 1 added these to the API response. Optional here so existing
  // hand-written Project fixtures keep compiling; the server always sends them.
  status?: ProjectStatus;
  statusUntil?: string | null;
  statusNote?: string | null;
}

/** `GET /api/projects` rows only; the single-project routes do not compute it. */
export interface ProjectListItem extends Project {
  /** When rows last landed in BigQuery. Null when nothing landed in the server's 30-day lookback. */
  lastDataSyncAt: string | null;
}

export interface Connector {
  id: string;
  name: string;
  type: string;
  prefix: string;
  projectId: string;
  vars: Record<string, unknown>;
  secrets: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface Destination {
  id: string;
  name: string;
  type: string;
  projectId: string;
  vars: Record<string, unknown>;
  secrets: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface ApiKey {
  id: string;
  name: string;
  kid: string;
  projectId: string;
  createdAt: string;
  /** Throttled server-side, so it can trail the key's real last request by a few minutes. */
  lastUsedAt: string | null;
}

export interface ApiKeyWithToken extends ApiKey {
  token: string;
}

export interface StreamState {
  id: string;
  connectorId: string;
  key: string;
  mode: "full" | "incremental";
  startState: Record<string, unknown>;
  nextState: Record<string, unknown>;
  createdAt: string;
}

export const CONNECTOR_TYPES = [
  { value: "powerschools", label: "PowerSchools" },
  { value: "powerschool-enrollment", label: "PowerSchool Enrollment" },
  { value: "skyward", label: "Skyward" },
  { value: "deanslist", label: "DeansList" },
  { value: "iready-sftp", label: "i-Ready", caption: "via SFTP" },
  { value: "schoolmint", label: "SchoolMint", caption: "via SFTP server" },
  { value: "avela", label: "Avela" },
  { value: "schoolmint-enroll", label: "SchoolMint Enroll", caption: "via SFTP server" },
  { value: "file-drop", label: "File Drop", caption: "via SFTP / upload" },
  { value: "dna", label: "Renaissance DnA", caption: "Illuminate assessments" },
] as const;

interface TransformDependency {
  id: string;
  sourceDataset: string;
  sourceTable: string;
}

export interface TransformDefinition {
  id: string;
  projectId: string;
  name: string;
  layer: string;
  outputDataset: string;
  outputTable: string;
  sqlTemplate: string;
  params: Record<string, string>;
  enabled: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
  dependencies: TransformDependency[];
}

export const LAYER_OPTIONS = [
  { value: "silver-canon", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "diamond", label: "Diamond" },
] as const;

export interface ExportDefinitionLatestRun {
  id: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  rowCount: number | null;
  errorMessage: string | null;
}

export interface ExportDefinition {
  id: string;
  projectId: string;
  name: string;
  enabled: boolean;
  cadence: string;
  format: string;
  filenameTemplate: string;
  destinationType: string;
  createdAt: string;
  updatedAt: string;
  latestRun: ExportDefinitionLatestRun | null;
}

/**
 * Full export definition returned by the `get` / `upsert` / `update` routes —
 * includes the SQL template and JSON config fields the list omits.
 */
export interface ExportDefinitionDetail {
  id: string;
  projectId: string;
  name: string;
  enabled: boolean;
  cadence: string;
  sqlTemplate: string;
  params: Record<string, string>;
  format: string;
  filenameTemplate: string;
  columnOrder: string[];
  destinationType: string;
  destinationVars: Record<string, unknown>;
  secretRef: string;
  createdAt: string;
  updatedAt: string;
}

export const EXPORT_CADENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "manual", label: "Manual" },
] as const;

export const EXPORT_FORMAT_OPTIONS = [
  { value: "csv", label: "CSV" },
  { value: "tsv", label: "TSV" },
  { value: "json", label: "JSON" },
] as const;

export const EXPORT_DESTINATION_TYPE_OPTIONS = [
  { value: "sftp", label: "SFTP" },
  { value: "gcs", label: "Google Cloud Storage" },
] as const;

export interface ArchiveMapping {
  sourceDataset: string;
  archiveDataset: string;
}

export interface ArchiveConfig {
  id: string;
  projectId: string;
  enabled: boolean;
  label: string;
  mappings: ArchiveMapping[];
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetailResponse {
  project: Project;
  connectors: Connector[];
  destinations: Destination[];
  apiKeys: ApiKey[];
  transformDefinitions: TransformDefinition[];
  exportDefinitions: ExportDefinition[];
  archiveConfig: ArchiveConfig | null;
}

export interface FeatureFlag {
  id: string;
  key: string;
  defaultValue: boolean;
  description: string;
  staleAfter: string | null;
  createdAt: string;
  updatedAt: string;
  overrideCount: number;
}

export interface FeatureFlagDetail {
  id: string;
  key: string;
  defaultValue: boolean;
  description: string;
  staleAfter: string | null;
  createdAt: string;
  updatedAt: string;
  overrides: FeatureFlagOverride[];
}

export interface FeatureFlagOverride {
  id: string;
  flagKey: string;
  projectId: string | null;
  projectName: string | null;
  tenantId: string | null;
  tenantName: string | null;
  value: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PortalTenant {
  id: string;
  name: string;
}

export interface AdhocTransformRun {
  id: number;
  projectId: string;
  // SIM-1189 PR B2: nullable for cascade-from-bronze rows.
  transformDefinitionId: string | null;
  table: string;
  dataset: string;
  // SIM-1189 PR B2: nullable for cascade rows (multi-step plan lives in runPlan).
  sql: string | null;
  status: string;
  requestedAt: string;
  claimedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  bytesProcessed: number | null;
}

export interface ConfigRevision {
  id: string;
  entityType: string;
  entityId: string;
  field: string;
  previousValue: Record<string, unknown>;
  createdAt: string;
  createdByEmail: string | null;
}

/**
 * Selectable destination types for the create form (SIM-2595). `bigquery` and
 * `bigquery-experimental` are deliberately absent: every live destination is `bigquery-nodedup` and
 * every row carrying the other two is archived, so offering them provisioned new clients onto an
 * unexercised path — with `bigquery` pre-selected. Both stay valid in `destinationSchema`.
 */
export const DESTINATION_TYPES = [
  {
    value: "bigquery-nodedup",
    label: "BigQuery",
    tooltip:
      "Inserts every snapshot with no deduplication. Requires paired Scheduled SQL that cleans duplicate snapshots to avoid major dataset bloat.",
  },
  { value: "example", label: "Example" },
] as const;
